import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Bank,
  Camera,
  CheckCircle,
  IdentificationCard,
  PencilSimple,
  Plant,
  Printer,
  QrCode,
  UsersThree,
} from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import {
  Banner,
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  Spinner,
  useToast,
  type Column,
} from '@/ui'
import { useScope } from '../api/appConfig'
import {
  missingRosterDetails,
  useFarmer,
  useFarmers,
  useUpdateFarmer,
  type Farmer,
  type FarmerMissing,
} from '../api/farmers'
import { useCan } from '@/auth/context'
import { PhotoViewer } from '../components/PhotoViewer'
import { errorMessage } from '../api/core'
import { useLots } from '../api/gate'
import {
  maskAccount,
  useApproveBankAccount,
  useBankAccounts,
  useFarmerBalance,
  useFarmerLedger,
  usePayouts,
  useStartVerification,
  PAYOUT_STATUS_LABEL,
  type BankAccount,
  type Verification,
} from '../api/payouts'
import { uploadEvidence, useVillages } from '../api/reference'
import type { LedgerLine } from '../api/gate'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { SearchFilter, SelectFilter } from '../components/FilterBar'
import { ConsolePage, DetailColumns, QueryState } from '../components/Layout'
import {
  Code,
  Facts,
  Money,
  RefLink,
  Section,
  StatTile,
  Status,
  UserName,
  When,
} from '../components/bits'
import { humanize } from '../labels'
import { useUrlState } from '../lib/useUrlState'

/**
 * Farmers (V1): the village's onboarded field users (villages are groups), found at the gate by
 * their farmer ID (e.g. RJ-AMOD-U001), name and photo. The gate buys from a farmer only what the
 * office marked ready for harvest (Ready for harvest page). Open one for his money and bank.
 */
export function FarmersPage() {
  const navigate = useNavigate()
  const villages = useVillages()
  const [f, setF] = useUrlState({
    q: '',
    groupId: '',
    missing: '' as FarmerMissing | '',
    page: '1',
  })
  const page = Number(f.page) || 1
  const q = useFarmers({
    q: f.q || undefined,
    groupId: f.groupId || undefined,
    missing: f.missing,
    page,
    limit: 50,
  })
  const columns: Column<Farmer>[] = [
    { id: 'code', header: 'Farmer ID', cell: (r) => <Code>{r.farmerCode ?? '—'}</Code> },
    {
      id: 'name',
      header: 'Name',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="font-semibold">{r.name}</span>
          {r.careOf && <span className="text-[12px] text-muted">s/o {r.careOf}</span>}
        </span>
      ),
    },
    { id: 'village', header: 'Village', cell: (r) => r.villageName ?? '—', hideBelow: 'sm' },
    {
      id: 'phone',
      header: 'Phone',
      cell: (r) => (r.contactNo ? <Code>••••{r.contactNo.slice(-4)}</Code> : '—'),
      hideBelow: 'md',
    },
    {
      id: 'bank',
      header: 'Bank',
      cell: (r) =>
        r.bankStatus ? (
          <Status
            status={r.bankStatus}
            label={r.bankStatus === 'VERIFIED' ? `•••• ${r.bankLast4 ?? ''}` : undefined}
          />
        ) : (
          <span className="text-[13px] text-muted">not checked</span>
        ),
      hideBelow: 'md',
    },
    {
      id: 'ready',
      header: 'Gate can buy',
      cell: (r) => {
        const missing = missingRosterDetails(r)
        return missing.length === 0 ? (
          <Status status="ACTIVE" label={`Yes · ${r.readyCultivations} ready`} />
        ) : (
          <span className="text-[13px] font-semibold text-warn">Needs {missing.join(' and ')}</span>
        )
      },
    },
  ]
  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Farmers"
      description="The village's farmers from onboarding, found at the gate by farmer ID, name and photo. The gate buys only crops marked ready for harvest."
      actions={
        <>
          <Button
            variant="secondary"
            leadingIcon={<Plant />}
            onClick={() =>
              navigate(`/c/harvest-readiness${f.groupId ? `?groupId=${f.groupId}` : ''}`)
            }
          >
            Ready for harvest
          </Button>
          {f.groupId && (
            <Button
              variant="secondary"
              leadingIcon={<Printer />}
              onClick={() => navigate(`/c/farmers/roster/${f.groupId}`)}
            >
              Print village list
            </Button>
          )}
        </>
      }
    >
      <DataTable<Farmer>
        caption="Farmers"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/farmers/${r.id}`)}
        pageSize={50}
        server={{ page, total: q.data?.total ?? 0, onPageChange: (p) => setF({ page: String(p) }) }}
        filters={
          <>
            <SearchFilter
              label="Find a farmer"
              value={f.q}
              onChange={(v) => setF({ q: v, page: '1' })}
              placeholder="Name, farmer ID or phone"
              className="min-w-[16rem]"
            />
            <SelectFilter
              label="Village"
              value={f.groupId}
              onChange={(v) => setF({ groupId: v, page: '1' })}
              options={(villages.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
              allLabel="All villages"
            />
            <SelectFilter<FarmerMissing>
              label="Show"
              value={f.missing}
              onChange={(v) => setF({ missing: v, page: '1' })}
              options={[
                { value: 'ANY', label: 'Gate cannot buy yet' },
                { value: 'READY', label: 'No crop ready for harvest' },
                { value: 'PHOTO', label: 'No photo' },
              ]}
              allLabel="Everyone"
            />
          </>
        }
        empty={
          <EmptyState
            icon={<UsersThree />}
            title={f.q || f.groupId || f.missing ? 'No farmer matches' : 'No farmers yet'}
            description={
              f.q
                ? 'Try his farmer ID (e.g. RJ-AMOD-U001), the last digits of it, or of his phone.'
                : 'Farmers come from onboarding (field users in a village).'
            }
          />
        }
      />
    </ConsolePage>
  )
}

/** What the gate checks him by: father's name, phone and photo. His ID, name and village come from onboarding. */
export function FarmerFormDialog({
  open,
  onClose,
  farmer,
}: {
  open: boolean
  onClose: () => void
  farmer: Farmer
}) {
  const toast = useToast()
  const update = useUpdateFarmer(farmer.id)
  const [careOf, setCareOf] = useState(farmer.careOf ?? '')
  const [contactNo, setContactNo] = useState(farmer.contactNo ?? '')
  const [photo, setPhoto] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [touched, setTouched] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const phone = contactNo.replace(/\D/g, '')
  const phoneError =
    phone && !/^[6-9]\d{9}$/.test(phone) ? '10 digits, starting with 6, 7, 8 or 9' : null
  const onPhoto = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      setPhoto(await uploadEvidence(file))
    } catch (e) {
      toast.error('Photo not uploaded', errorMessage(e))
    } finally {
      setUploading(false)
    }
  }
  const submit = async () => {
    setTouched(true)
    setServerError(null)
    if (phoneError) return
    try {
      await update.mutateAsync({
        careOf: careOf.trim(),
        contactNo: phone,
        ...(photo ? { photoMediaId: photo } : {}),
      })
      onClose()
    } catch (e) {
      setServerError(errorMessage(e))
    }
  }
  const shownPhoto = photo ?? farmer.photoId ?? null
  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!update.isPending}
      title={`${farmer.name} · ${farmer.farmerCode ?? ''}`}
      description="What the gate checks him by. His farmer ID, name and village come from onboarding. Phones get changes with their next roster download."
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button loading={update.isPending} disabled={uploading} onClick={() => void submit()}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Father's name" hint="Tells two farmers with the same name apart.">
          <Input value={careOf} onChange={(e) => setCareOf(e.target.value)} maxLength={80} />
        </Field>
        <Field
          label="His phone"
          hint="His own phone: payment SMS go here."
          error={touched ? phoneError : null}
        >
          <Input
            value={contactNo}
            onChange={(e) => setContactNo(e.target.value.replace(/[^\d ]/g, ''))}
            inputMode="numeric"
            maxLength={12}
            className="font-mono"
          />
        </Field>
        <Field
          label="Photo"
          hint="His face, clear and front-on: the operator checks it at the gate."
          className="sm:col-span-2"
        >
          <div className="flex items-center gap-3">
            {shownPhoto && <PhotoViewer photo={shownPhoto} alt={farmer.name} size="sm" />}
            <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-chip border border-ink/80 px-3 font-mono text-[11px] font-bold uppercase tracking-label hover:bg-ink hover:text-paper">
              {uploading ? <Spinner /> : <Camera aria-hidden="true" />}
              {uploading ? 'Uploading…' : shownPhoto ? 'Change photo' : 'Add photo'}
              <input
                type="file"
                accept="image/*"
                capture="user"
                className="sr-only"
                onChange={(e) => void onPhoto(e.target.files?.[0])}
              />
            </label>
          </div>
        </Field>
      </div>
      {serverError && (
        <Banner tone="error" className="mt-4">
          {serverError}
        </Banner>
      )}
    </Modal>
  )
}

export function FarmerPage() {
  const { id } = useParams()
  const { v1 } = useScope()
  const can = useCan()
  const farmer = useFarmer(id)
  const balance = useFarmerBalance(id)
  const [editing, setEditing] = useState(false)
  const fd = farmer.data
  const missing = fd ? missingRosterDetails(fd) : []
  return (
    <ConsolePage
      back={{ to: '/c/farmers', label: 'Farmers' }}
      eyebrow={fd?.farmerCode ?? 'Farmer'}
      title={fd?.name ?? 'Farmer'}
      description="Roster details for the phone, and his money: every ledger line is kept, corrections are new lines."
      actions={
        fd && can('farmers:update') ? (
          <Button
            variant="secondary"
            leadingIcon={<PencilSimple />}
            onClick={() => setEditing(true)}
          >
            Edit details
          </Button>
        ) : undefined
      }
    >
      {fd && (
        <Section title="At the gate" eyebrow="What the phone shows">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            {fd.photoId ? (
              <PhotoViewer photo={fd.photoId} alt={fd.name} size="md" />
            ) : (
              <div className="grid h-24 w-24 place-items-center rounded-card border border-dashed border-rule text-[12px] text-muted">
                No photo
              </div>
            )}
            <Facts
              columns={3}
              items={[
                { label: 'Farmer ID', value: fd.farmerCode ? <Code>{fd.farmerCode}</Code> : '—' },
                {
                  label: 'Ready for harvest',
                  value: fd.readyCultivations ? (
                    `${fd.readyCultivations} cultivation${fd.readyCultivations === 1 ? '' : 's'}`
                  ) : (
                    <Link
                      className="underline"
                      to={`/c/harvest-readiness?groupId=${fd.groupId ?? ''}`}
                    >
                      None: mark one
                    </Link>
                  ),
                },
                { label: "Father's name", value: fd.careOf ?? '—' },
                { label: 'Village', value: fd.villageName ?? '—' },
                { label: 'Phone', value: fd.contactNo ? <Code>{fd.contactNo}</Code> : '—' },
                {
                  label: 'Bank',
                  value: fd.bankStatus
                    ? `${humanize(fd.bankStatus)}${fd.bankLast4 ? ` · •••• ${fd.bankLast4}` : ''}`
                    : 'Not checked',
                },
              ]}
            />
          </div>
          {missing.length > 0 && (
            <Banner tone="warn" className="mt-3">
              The gate cannot buy from him yet: he needs {missing.join(' and ')}.
            </Banner>
          )}
        </Section>
      )}
      <QueryState query={balance} label="the balance">
        {balance.data && (
          <div
            className={
              v1 ? 'grid grid-cols-1 gap-3 sm:grid-cols-2' : 'grid grid-cols-1 gap-3 sm:grid-cols-3'
            }
          >
            <StatTile label="Owed on lots" value={<Money paise={balance.data.owedOnLotsPaise} />} />
            {!v1 && (
              <StatTile
                label="Advance outstanding"
                value={<Money paise={balance.data.advanceOutstandingPaise} />}
                tone={balance.data.advanceOutstandingPaise > 0 ? 'warn' : 'neutral'}
              />
            )}
            <StatTile
              label="Net position"
              value={<Money paise={balance.data.netPositionPaise} />}
              hint={
                balance.data.lastEntryAt ? (
                  <>
                    Last entry <When at={balance.data.lastEntryAt} dateOnly />
                  </>
                ) : undefined
              }
            />
          </div>
        )}
      </QueryState>
      {id && (
        <DetailColumns
          main={
            <>
              <LedgerSection farmerId={id} />
              <FarmerPayouts farmerId={id} />
              <FarmerLots farmerId={id} />
            </>
          }
          side={
            <>
              <BankAccountsPanel farmerId={id} />
              {balance.data && balance.data.openLots.length > 0 && (
                <Section title="Open lots" eyebrow="Balance per lot">
                  <ul className="flex flex-col gap-1.5">
                    {balance.data.openLots.map((l) => (
                      <li key={l.unitId} className="flex items-baseline justify-between gap-2">
                        <RefLink to={`/c/lots/${l.unitId}`}>{l.unitCode}</RefLink>
                        <Money paise={l.balancePaise} signed />
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          }
        />
      )}
      {editing && fd && <FarmerFormDialog open farmer={fd} onClose={() => setEditing(false)} />}
    </ConsolePage>
  )
}

function LedgerSection({ farmerId }: { farmerId: string }) {
  const [page, setPage] = useState(1)
  const q = useFarmerLedger(farmerId, { page, limit: 25 })
  const columns: Column<LedgerLine>[] = [
    { id: 'at', header: 'When', cell: (l) => <When at={l.occurredAt} /> },
    { id: 'kind', header: 'Kind', cell: (l) => humanize(l.kind) },
    {
      id: 'lot',
      header: 'Lot',
      cell: (l) =>
        l.unitId ? <RefLink to={`/c/lots/${l.unitId}`}>{l.unitCode ?? 'lot'}</RefLink> : '—',
      hideBelow: 'sm',
    },
    {
      id: 'note',
      header: 'Note',
      cell: (l) => <span className="text-[13px] text-muted">{l.note ?? ''}</span>,
      hideBelow: 'lg',
    },
    {
      id: 'amount',
      header: 'Amount',
      align: 'right',
      cell: (l) => <Money paise={l.amountPaise} signed />,
    },
  ]
  return (
    <DataTable<LedgerLine>
      caption="Ledger"
      columns={columns}
      rows={q.data?.rows ?? []}
      getRowId={(l) => l.id}
      loading={q.isLoading}
      error={q.isError ? errorMessage(q.error) : undefined}
      pageSize={25}
      server={{ page, total: q.data?.total ?? 0, onPageChange: setPage }}
      filters={
        <span className="font-mono text-[11px] font-bold uppercase tracking-label text-muted">
          Ledger
        </span>
      }
      empty={<EmptyState compact title="No ledger lines yet" />}
    />
  )
}

function FarmerPayouts({ farmerId }: { farmerId: string }) {
  const navigate = useNavigate()
  const q = usePayouts({ farmerId, page: 1, limit: 20 })
  return (
    <Section title="Payments" eyebrow="Latest 20" bodyClassName="p-0">
      {(q.data?.rows ?? []).length === 0 ? (
        <p className="px-4 py-3 text-[14px] text-muted">
          {q.isLoading ? 'Loading…' : 'No payments yet.'}
        </p>
      ) : (
        <ul className="divide-y divide-rule/70">
          {q.data!.rows.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => navigate(`/c/payouts?open=${p.id}`)}
                className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-paper-tint/60"
              >
                <span className="flex items-center gap-2">
                  <Code>{p.unitCode}</Code>
                  {p.kind === 'TOP_UP' && <span className="text-[12px] text-info">top-up</span>}
                  <Status status={p.status} label={PAYOUT_STATUS_LABEL[p.status]} />
                </span>
                <Money paise={p.amountPaise} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

function FarmerLots({ farmerId }: { farmerId: string }) {
  const q = useLots({ farmerId, page: 1, limit: 20 })
  return (
    <Section title="Lots" eyebrow="Latest 20" bodyClassName="p-0">
      {(q.data?.rows ?? []).length === 0 ? (
        <p className="px-4 py-3 text-[14px] text-muted">
          {q.isLoading ? 'Loading…' : 'No lots yet.'}
        </p>
      ) : (
        <ul className="divide-y divide-rule/70">
          {q.data!.rows.map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
              <span className="flex items-center gap-2">
                <RefLink to={`/c/lots/${l.id}`}>{l.code}</RefLink>
                <span className="text-[12px] text-muted">{l.cropName}</span>
                {l.hold !== 'NONE' && <Status status={l.hold} />}
              </span>
              <Money paise={l.amountPaise} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

/**
 * Farmers · Bank account (F4-B4.c, F4.9-n): only last 4 digits, IFSC and the name at the bank are
 * ever shown. OnGrid verifies the passbook details, then a second person approves the new account
 * as primary. The full number is encrypted server-side for a future controlled payout export.
 */
function BankAccountsPanel({ farmerId }: { farmerId: string }) {
  const { user } = useAuth()
  const q = useBankAccounts(farmerId)
  const approve = useApproveBankAccount(farmerId)
  const [verify, setVerify] = useState(false)
  const [approving, setApproving] = useState<BankAccount | null>(null)
  const [nameMatches, setNameMatches] = useState(false)
  return (
    <Section
      id="bank"
      title="Bank accounts"
      eyebrow="Last 4 digits only"
      actions={
        <>
          <Link
            to={`/c/farmers/${farmerId}/bank`}
            className="text-[13px] underline decoration-rule underline-offset-2 hover:decoration-amber"
          >
            Bank account screen
          </Link>
          <ActionButton
            perm="bank-accounts:verify"
            size="sm"
            leadingIcon={<QrCode />}
            onClick={() => setVerify(true)}
          >
            Verify account
          </ActionButton>
        </>
      }
      bodyClassName="p-0"
    >
      <QueryState query={q} label="bank accounts">
        {(q.data ?? []).length === 0 ? (
          <p className="px-4 py-3 text-[14px] text-muted">No bank account yet.</p>
        ) : (
          <ul className="divide-y divide-rule/70">
            {q.data!.map((a) => (
              <li key={a.id} className="flex flex-col gap-1.5 px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <Bank aria-hidden="true" className="text-muted" />
                    <span className="font-mono text-[14px]">{maskAccount(a.accountLast4)}</span>
                    <span className="font-mono text-[12px] text-muted">{a.ifsc}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    {a.isPrimary && <Status status="ACTIVE" label="Primary" />}
                    <Status status={a.status === 'FAILED' ? 'FAILED' : a.status} />
                  </span>
                </div>
                <Facts
                  columns={2}
                  items={[
                    { label: 'Name at bank', value: a.nameAtBank ?? '—' },
                    {
                      label: 'Checked by',
                      value: a.verificationMethod ? humanize(a.verificationMethod) : '—',
                    },
                    { label: 'Started by', value: <UserName id={a.addedById} /> },
                    {
                      label: 'Approved by',
                      value: a.approvedById ? <UserName id={a.approvedById} /> : '—',
                    },
                  ]}
                />
                {a.status === 'PENDING' && (
                  <div className="pt-1">
                    <ActionButton
                      perm="bank-accounts:approve"
                      size="sm"
                      leadingIcon={<CheckCircle />}
                      blockedReason={
                        user && a.addedById === user.id
                          ? 'You started this check: a second person must approve the account.'
                          : !a.nameAtBank
                            ? 'No name at bank yet.'
                            : null
                      }
                      onClick={() => {
                        setNameMatches(false)
                        setApproving(a)
                      }}
                    >
                      Approve
                    </ActionButton>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </QueryState>
      <VerifyDialog open={verify} onClose={() => setVerify(false)} farmerId={farmerId} />
      <ConfirmDialog
        open={Boolean(approving)}
        onClose={() => setApproving(null)}
        title="Approve the new account"
        description="It becomes his primary account; the old one is disabled and he gets an SMS that his account changed."
        confirmLabel="Approve as primary"
        loading={approve.isPending}
        disabledReason={
          nameMatches
            ? null
            : 'Tick “Name matches” after comparing the name at the bank with the farmer.'
        }
        onConfirm={() =>
          approving ? approve.mutateAsync(approving.id).then(() => setApproving(null)) : undefined
        }
      >
        {approving && (
          <>
            <Facts
              columns={2}
              items={[
                {
                  label: 'New account',
                  value: `${maskAccount(approving.accountLast4)} · ${approving.ifsc ?? ''}`,
                },
                { label: 'Name at bank', value: approving.nameAtBank ?? '—' },
              ]}
            />
            <label className="flex items-center gap-2 text-[14px] font-semibold">
              <input
                type="checkbox"
                className="h-4 w-4 accent-amber"
                checked={nameMatches}
                onChange={(e) => setNameMatches(e.target.checked)}
              />
              Name matches the farmer
            </label>
          </>
        )}
      </ConfirmDialog>
    </Section>
  )
}

export function VerifyDialog({
  open,
  onClose,
  farmerId,
}: {
  open: boolean
  onClose: () => void
  farmerId: string
}) {
  const [accountNumber, setAccountNumber] = useState('')
  const [ifsc, setIfsc] = useState('')
  const [nameOnPassbook, setName] = useState('')
  const [consent, setConsent] = useState(false)
  const [v, setV] = useState<Verification | null>(null)
  const start = useStartVerification(farmerId)
  const accOk = /^\d{6,18}$/.test(accountNumber)
  const ifscOk = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)
  const close = () => {
    setV(null)
    setAccountNumber('')
    setIfsc('')
    setName('')
    setConsent(false)
    onClose()
  }
  const go = async () => {
    try {
      const r = await start.mutateAsync({
        accountNumber,
        ifsc,
        nameOnPassbook: nameOnPassbook || undefined,
        consent: true,
      })
      // Clear the full account number immediately after it has been submitted.
      setAccountNumber('')
      setV(r)
    } catch {
      /* toasted */
    }
  }
  return (
    <Modal
      open={open}
      onClose={close}
      title="Verify bank account"
      description="OnGrid checks the account. The verified result waits for a second person's approval."
      footer={
        v ? (
          <>
            <Button onClick={close}>Done</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" className="ml-auto" onClick={close}>
              Cancel
            </Button>
            <Button
              loading={start.isPending}
              disabled={!accOk || !ifscOk || !consent}
              onClick={() => void go()}
            >
              Start check
            </Button>
          </>
        )
      }
    >
      {v ? (
        <div className="flex flex-col gap-3">
          <Facts
            columns={2}
            items={[
              {
                label: 'OnGrid result',
                value: <Status status={(v.accountStatus ?? v.status).toUpperCase()} />,
              },
              {
                label: 'Account',
                value: v.accountLast4 ? maskAccount(v.accountLast4) : '—',
              },
              { label: 'Name at bank', value: v.nameAtBank ?? 'Not returned' },
              { label: 'Bank', value: v.bankName ?? '—' },
              { label: 'IFSC', value: v.ifsc ?? '—' },
              {
                label: 'Branch',
                value: [v.branch, v.city].filter(Boolean).join(' · ') || '—',
              },
              { label: 'Check reference', value: <Code>{v.validationId}</Code> },
            ]}
          />
          {v.accountId && (
            <Banner tone="success">
              OnGrid verified these details. Confirm them with the farmer; a second office person
              can then approve the account.
            </Banner>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Account number"
              required
              error={accountNumber && !accOk ? '6–18 digits' : null}
            >
              <Input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                autoComplete="off"
                type="password"
                className="font-mono"
                leading={<IdentificationCard />}
              />
            </Field>
            <Field label="IFSC" required error={ifsc && !ifscOk ? 'Like SBIN0001234' : null}>
              <Input
                value={ifsc}
                onChange={(e) => setIfsc(e.target.value.toUpperCase())}
                className="font-mono"
                maxLength={11}
                autoComplete="off"
              />
            </Field>
            <Field label="Name on passbook" className="sm:col-span-2">
              <Input
                value={nameOnPassbook}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
            </Field>
          </div>
          <label className="flex items-start gap-2 rounded-card border border-rule px-3 py-2 text-[14px]">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-amber"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>The farmer has consented to verification of these bank-account details.</span>
          </label>
          <Banner tone="info">
            The verified account number is encrypted on the server. Screens continue to show only
            its last four digits.
          </Banner>
        </div>
      )}
    </Modal>
  )
}
